
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Search,
  TrendingUp,
  TrendingDown,
  RotateCcw,
  MapPin,
  Package,
  Loader2,
} from 'lucide-react';
import { stockAdjustmentsAPI, inventoriesAPI } from '../../services/garageApi';
import toast from 'react-hot-toast';

const ADJUSTMENT_TYPES = [
  { value: 'increase', label: 'Increase Stock', icon: TrendingUp, color: 'border-green-300 bg-green-50 text-green-700', description: 'Add units to inventory' },
  { value: 'decrease', label: 'Decrease Stock', icon: TrendingDown, color: 'border-red-300 bg-red-50 text-red-700', description: 'Remove units from inventory' },
  { value: 'correction', label: 'Correction', icon: RotateCcw, color: 'border-blue-300 bg-blue-50 text-blue-700', description: 'Set to exact count after physical check' },
];

const REASON_PRESETS = [
  'Damaged Goods',
  'Inventory Count Correction',
  'Quality Control',
  'Returned to Supplier',
  'Customer Return',
  'System Correction',
  'Expired Stock',
  'Theft / Loss',
];

const CreateStockAdjustment = () => {
  const navigate = useNavigate();

  // Form state
  const [adjustmentType, setAdjustmentType] = useState('');
  const [inventoryId, setInventoryId] = useState('');
  const [quantity, setQuantity] = useState('');
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');

  // Inventory search
  const [inventories, setInventories] = useState([]);
  const [inventorySearch, setInventorySearch] = useState('');
  const [inventoryLoading, setInventoryLoading] = useState(true);
  const [selectedInventory, setSelectedInventory] = useState(null);

  // Submit state
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Load all inventories on mount
  useEffect(() => {
    const fetchInventories = async () => {
      try {
        setInventoryLoading(true);
        const response = await inventoriesAPI.getAll({ per_page: 200 });
        const data = Array.isArray(response.data) ? response.data : [];
        setInventories(data);
      } catch (err) {
        console.error('Error loading inventories:', err);
      } finally {
        setInventoryLoading(false);
      }
    };
    fetchInventories();
  }, []);

  // Filter inventories by search term
  const filteredInventories = inventories.filter((inv) => {
    if (!inventorySearch) return true;
    const term = inventorySearch.toLowerCase();
    const product = inv.inventoryable || inv.product || {};
    const productName = product.size || product.battery_name || product.name || '';
    const locationName = inv.location?.name || '';
    const zoneName = inv.zone?.name || '';
    return (
      productName.toLowerCase().includes(term) ||
      locationName.toLowerCase().includes(term) ||
      zoneName.toLowerCase().includes(term) ||
      inv.id?.toLowerCase().includes(term)
    );
  });

  const getProductName = (inv) => {
    const product = inv.inventoryable || inv.product || {};
    if (product.size) return product.size + (product.type ? ' ' + product.type : '');
    if (product.battery_name) return product.battery_name;
    if (product.name) return product.name;
    return 'Unknown Product';
  };

  const getProductType = (inv) => {
    const type = inv.inventoryable_type;
    if (type === 'tire' || type?.includes('Tire')) return 'tire';
    if (type === 'battery' || type?.includes('Battery')) return 'battery';
    if (type === 'wiper' || type?.includes('Wiper')) return 'wiper';
    return type || 'unknown';
  };

  const handleSelectInventory = (inv) => {
    setSelectedInventory(inv);
    setInventoryId(inv.id);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!inventoryId) {
      setError('Please select an inventory record');
      return;
    }
    if (!adjustmentType) {
      setError('Please select an adjustment type');
      return;
    }
    if (!quantity || parseInt(quantity) < 1) {
      setError('Please enter a valid quantity (minimum 1)');
      return;
    }

    // For decrease, validate against available stock
    if (adjustmentType === 'decrease' && selectedInventory) {
      const available = selectedInventory.available_quantity ?? selectedInventory.quantity ?? 0;
      if (parseInt(quantity) > available) {
        setError(`Cannot decrease by ${quantity}. Only ${available} units available.`);
        return;
      }
    }

    try {
      setSubmitting(true);
      await stockAdjustmentsAPI.create({
        inventory_id: inventoryId,
        adjustment_type: adjustmentType,
        quantity: parseInt(quantity),
        reason: reason || null,
        notes: notes || null,
      });

      toast.success('Stock adjustment created successfully');
      navigate('/stock-adjustments');
    } catch (err) {
      console.error('Error creating adjustment:', err);
      if (err.response?.data?.errors) {
        const msgs = Object.values(err.response.data.errors).flat().join(', ');
        setError(msgs);
      } else {
        setError(err.response?.data?.message || 'Failed to create stock adjustment');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/stock-adjustments')}
          className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-3"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Adjustments
        </button>
        <h1 className="text-2xl font-bold text-gray-900">New Stock Adjustment</h1>
        <p className="text-gray-600 mt-1">Manually adjust inventory quantities</p>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 border border-red-200 p-4 mb-6">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Step 1: Select Adjustment Type */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4">
            1. Adjustment Type
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {ADJUSTMENT_TYPES.map((type) => {
              const Icon = type.icon;
              const isSelected = adjustmentType === type.value;
              return (
                <button
                  key={type.value}
                  type="button"
                  onClick={() => setAdjustmentType(type.value)}
                  className={`p-4 rounded-lg border-2 text-left transition-all ${
                    isSelected
                      ? type.color + ' border-current shadow-sm'
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Icon className={`h-5 w-5 ${isSelected ? '' : 'text-gray-400'}`} />
                    <span className="font-medium text-sm">{type.label}</span>
                  </div>
                  <p className={`text-xs ${isSelected ? 'opacity-80' : 'text-gray-400'}`}>
                    {type.description}
                  </p>
                </button>
              );
            })}
          </div>
        </div>

        {/* Step 2: Select Inventory Record */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4">
            2. Select Product &amp; Location
          </h2>

          {/* Search */}
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={inventorySearch}
              onChange={(e) => setInventorySearch(e.target.value)}
              placeholder="Search by product name, location, or zone..."
              className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
          </div>

          {/* Inventory List */}
          <div className="border rounded-lg max-h-64 overflow-y-auto divide-y divide-gray-100">
            {inventoryLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                <span className="ml-2 text-sm text-gray-500">Loading inventory...</span>
              </div>
            ) : filteredInventories.length === 0 ? (
              <div className="py-8 text-center text-sm text-gray-400">
                {inventorySearch ? 'No matching inventory records' : 'No inventory records found'}
              </div>
            ) : (
              filteredInventories.map((inv) => {
                const isSelected = inventoryId === inv.id;
                const productType = getProductType(inv);
                return (
                  <button
                    key={inv.id}
                    type="button"
                    onClick={() => handleSelectInventory(inv)}
                    className={`w-full px-4 py-3 text-left flex items-center justify-between hover:bg-gray-50 transition-colors ${
                      isSelected ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                    }`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <Package className={`h-5 w-5 flex-shrink-0 ${isSelected ? 'text-blue-600' : 'text-gray-300'}`} />
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-gray-900 truncate">
                            {getProductName(inv)}
                          </span>
                          <span className={`inline-block px-1.5 py-0.5 rounded text-xs font-medium ${
                            productType === 'tire' ? 'bg-green-100 text-green-700' :
                            productType === 'battery' ? 'bg-blue-100 text-blue-700' :
                            productType === 'wiper' ? 'bg-purple-100 text-purple-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {productType}
                          </span>
                        </div>
                        <div className="flex items-center gap-3 mt-0.5">
                          {inv.location?.name && (
                            <span className="flex items-center gap-1 text-xs text-gray-400">
                              <MapPin className="h-3 w-3" />
                              {inv.location.name}
                            </span>
                          )}
                          {inv.zone?.name && (
                            <span className="text-xs text-gray-400">
                              {inv.zone.name}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0 ml-4">
                      <p className="text-sm font-semibold text-gray-900">{inv.quantity ?? 0}</p>
                      <p className="text-xs text-gray-400">in stock</p>
                    </div>
                  </button>
                );
              })
            )}
          </div>

          {selectedInventory && (
            <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200 text-sm">
              <span className="font-medium text-blue-800">Selected:</span>{' '}
              <span className="text-blue-700">
                {getProductName(selectedInventory)} — {selectedInventory.location?.name}
                {selectedInventory.zone?.name ? ` / ${selectedInventory.zone.name}` : ''}
                {' '}({selectedInventory.quantity ?? 0} units)
              </span>
            </div>
          )}
        </div>

        {/* Step 3: Quantity & Reason */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4">
            3. Quantity &amp; Reason
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Quantity <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min="1"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                placeholder="Enter quantity"
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              />
              {adjustmentType === 'decrease' && selectedInventory && quantity && (
                <p className="text-xs text-gray-500 mt-1">
                  Stock will change: {selectedInventory.quantity ?? 0} &rarr;{' '}
                  <span className={parseInt(quantity) > (selectedInventory.available_quantity ?? 0) ? 'text-red-600 font-medium' : 'text-gray-700'}>
                    {(selectedInventory.quantity ?? 0) - parseInt(quantity || 0)}
                  </span>
                </p>
              )}
              {adjustmentType === 'increase' && selectedInventory && quantity && (
                <p className="text-xs text-gray-500 mt-1">
                  Stock will change: {selectedInventory.quantity ?? 0} &rarr;{' '}
                  <span className="text-green-600 font-medium">
                    {(selectedInventory.quantity ?? 0) + parseInt(quantity || 0)}
                  </span>
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
              <select
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              >
                <option value="">Select a reason...</option>
                {REASON_PRESETS.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
                <option value="__custom">Other (type below)</option>
              </select>
              {reason === '__custom' && (
                <input
                  type="text"
                  value=""
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Type custom reason..."
                  className="mt-2 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                />
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Additional notes about this adjustment..."
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
          </div>
        </div>

        {/* Submit */}
        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate('/stock-adjustments')}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting || !adjustmentType || !inventoryId || !quantity}
            className="inline-flex items-center px-6 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {submitting ? 'Saving...' : 'Create Adjustment'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateStockAdjustment;
